import React, { createContext, useContext, useState, useEffect } from 'react';
import io from 'socket.io-client';
import api from '../services/api';

const GameContext = createContext();

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within GameProvider');
  }
  return context;
};

export const GameProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [currentSession, setCurrentSession] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teams, setTeams] = useState([]);
  const [finalGameWinner, setFinalGameWinner] = useState(null);

  // Helper to fetch current session
  const fetchCurrentSession = async () => {
    try {
      const response = await api.get('/game/current');
      if (response.data.success) {
        setCurrentSession(response.data.session);
      }
    } catch (error) {
      console.error('Error fetching session:', error);
    }
  };

  // Initialize socket connection
  useEffect(() => {
    const newSocket = io(process.env.REACT_APP_API_URL || 'http://localhost:5000', {
      transports: ['websocket', 'polling']
    });

    newSocket.on('connect', () => {
      console.log('✅ Connected to server');
      setIsConnected(true);
    });

    newSocket.on('disconnect', () => {
      console.log('❌ Disconnected from server');
      setIsConnected(false);
    });

    // Listen for session updates
    newSocket.on('session:current', (data) => {
      setCurrentSession(data.session);
    });

    newSocket.on('game:started', (data) => {
      setCurrentSession(data.session);
    });

    newSocket.on('game:activated', (data) => {
      setCurrentSession(data.session);
    });

    newSocket.on('game:paused', (data) => {
      setCurrentSession(data.session);
    });

    newSocket.on('game:resumed', (data) => {
      setCurrentSession(data.session);
    });

    newSocket.on('game:ended', (data) => {
      setCurrentSession(data.session);
      setSelectedTeam(null);
    });

    newSocket.on('tablets:locked', (data) => {
      // Use session from server if provided, otherwise update current session using functional update
      if (data.session) {
        setCurrentSession(data.session);
      } else {
        setCurrentSession(prev => prev ? { ...prev, tabletsLocked: true } : null);
      }
    });

    newSocket.on('tablets:unlocked', (data) => {
      // Use session from server if provided, otherwise update current session using functional update
      if (data.session) {
        setCurrentSession(data.session);
      } else {
        setCurrentSession(prev => prev ? { ...prev, tabletsLocked: false } : null);
      }
    });

    newSocket.on('final-game:started', (data) => {
      setCurrentSession(data.session);
    });

    newSocket.on('final-game:score-updated', () => {
      // Trigger session refresh to get updated scores
      fetchCurrentSession();
    });

    // Listen for score updates (when answers are submitted)
    newSocket.on('score:updated', () => {
      // Trigger session refresh to get updated session with answered questions
      fetchCurrentSession();
    });

    newSocket.on('final-game:ended', (data) => {
      // Update session with final game results
      setCurrentSession(data.session);
      // Store winner information
      if (data.winner) {
        setFinalGameWinner(data.winner);
      }
    });

    newSocket.on('game:terminated', (data) => {
      setCurrentSession(data.session || null);
      setSelectedTeam(null);
      // Clear loading state when game is terminated
      localStorage.removeItem('finalGameLoading');
    });

    newSocket.on('game:started', (data) => {
      setCurrentSession(data.session);
      // Clear loading state when new game starts
      localStorage.removeItem('finalGameLoading');
    });

    // Listen for next round event
    newSocket.on('round:next', (data) => {
      console.log('🔄 Round changed:', data);
      setCurrentSession(data.session);
    });

    // Listen for dining room question 4 pending (global listener for all components)
    newSocket.on('dining-room:question4:pending', (data) => {
      console.log('🌐 Global listener received dining-room:question4:pending:', data);
      // This event will be handled by Dashboard component
      // We just log it here to verify it's being received
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, []);

  // Fetch teams
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const response = await api.get('/team');
        if (response.data.success) {
          setTeams(response.data.teams);
        }
      } catch (error) {
        console.error('Error fetching teams:', error);
      }
    };

    fetchTeams();
  }, []);

  // Register device (tablet or dashboard)
  const registerDevice = (type, section = null) => {
    if (socket && isConnected) {
      socket.emit('register', { type, section });
    }
  };

  // Select team
  const selectTeam = (teamId) => {
    if (socket && currentSession) {
      return new Promise((resolve) => {
        socket.emit('team:select', {
          sessionId: currentSession.sessionId,
          teamId,
          section: null // Will be set by tablet
        });

        socket.once('team:selected', (data) => {
          if (data.success) {
            setSelectedTeam(data.team);
            resolve(true);
          } else {
            resolve(false);
          }
        });

        socket.once('error', () => {
          resolve(false);
        });
      });
    }
    return Promise.resolve(false);
  };

  // Submit answer
  const submitAnswer = (section, questionId, answer, cabinet = null) => {
    if (!socket) {
      console.error('❌ Submit answer failed: Socket not connected');
      return Promise.resolve({ error: 'Socket not connected' });
    }
    if (!currentSession) {
      console.error('❌ Submit answer failed: No current session');
      return Promise.resolve({ error: 'No active session' });
    }
    if (!selectedTeam) {
      console.error('❌ Submit answer failed: No team selected');
      console.error('Current session:', currentSession);
      return Promise.resolve({ error: 'No team selected. Please refresh the page.' });
    }

    console.log('✅ Submitting answer for team:', selectedTeam.name, 'in section:', section);
    
    return new Promise((resolve) => {
      socket.emit('answer:submit', {
        sessionId: currentSession.sessionId,
        teamId: selectedTeam.id,
        section,
        questionId,
        answer,
        cabinet
      });

      socket.once('answer:result', (result) => {
        resolve(result);
      });

      socket.once('error', (error) => {
        resolve({ error: error.message });
      });
    });
  };

  // Submit dining room question 4 (text answer for gamemaster approval)
  const submitDiningRoomQuestion4 = (answer) => {
    if (!socket) {
      console.error('❌ Submit dining room Q4 failed: Socket not connected');
      return Promise.resolve({ error: 'Socket not connected' });
    }
    if (!currentSession) {
      console.error('❌ Submit dining room Q4 failed: No current session');
      return Promise.resolve({ error: 'No active session' });
    }
    if (!selectedTeam) {
      console.error('❌ Submit dining room Q4 failed: No team selected');
      return Promise.resolve({ error: 'No team selected. Please refresh the page.' });
    }

    console.log('✅ Submitting dining room Q4 for team:', selectedTeam.name);
    
    return new Promise((resolve) => {
      socket.emit('dining-room:question4:submit', {
        sessionId: currentSession.sessionId,
        teamId: selectedTeam.id,
        answer: answer.trim()
      });

      socket.once('dining-room:question4:result', (result) => {
        resolve(result);
      });

      socket.once('error', (error) => {
        resolve({ error: error.message });
      });
    });
  };

  // Request audio replay
  const requestAudioReplay = (section) => {
    if (socket && currentSession && selectedTeam) {
      return new Promise((resolve) => {
        socket.emit('audio:replay', {
          sessionId: currentSession.sessionId,
          teamId: selectedTeam.id,
          section
        });

        socket.once('audio:replay-allowed', (data) => {
          resolve(data);
        });

        socket.once('audio:penalty', (data) => {
          resolve(data);
        });
      });
    }
    return Promise.resolve({ error: 'Not connected' });
  };

  // Request help
  const requestHelp = (section) => {
    if (socket && currentSession && selectedTeam) {
      socket.emit('help:request', {
        sessionId: currentSession.sessionId,
        teamId: selectedTeam.id,
        section
      });
    }
  };

  // Confirm final game answer (stores answer, doesn't submit yet)
  const confirmFinalGameAnswer = (answer) => {
    if (socket && currentSession && selectedTeam) {
      return new Promise((resolve) => {
        socket.emit('final-game:confirm', {
          sessionId: currentSession.sessionId,
          teamId: selectedTeam.id,
          answer
        });

        socket.once('final-game:confirmed', (data) => {
          resolve(data);
        });

        socket.once('error', (error) => {
          resolve({ error: error.message });
        });
      });
    }
    return Promise.resolve({ error: 'Not connected' });
  };

  // Submit final game answer (called when timer ends)
  const submitFinalGameAnswer = (questionId, answer) => {
    if (socket && currentSession && selectedTeam) {
      return new Promise((resolve) => {
        socket.emit('final-game:answer', {
          sessionId: currentSession.sessionId,
          teamId: selectedTeam.id,
          questionId,
          answer
        });

        socket.once('final-game:result', (data) => {
          resolve(data);
        });

        socket.once('error', (error) => {
          resolve({ error: error.message });
        });
      });
    }
    return Promise.resolve({ error: 'Not connected' });
  };

  // Move to next question (called when timer ends)
  const moveToNextFinalGameQuestion = () => {
    if (socket && currentSession && selectedTeam) {
      return new Promise((resolve) => {
        socket.emit('final-game:next-question', {
          sessionId: currentSession.sessionId,
          teamId: selectedTeam.id
        });

        socket.once('final-game:next', (data) => {
          resolve(data);
        });

        socket.once('error', (error) => {
          resolve({ error: error.message });
        });
      });
    }
    return Promise.resolve({ error: 'Not connected' });
  };

  const value = {
    socket,
    isConnected,
    currentSession,
    selectedTeam,
    teams,
    finalGameWinner,
    registerDevice,
    selectTeam,
    submitAnswer,
    submitDiningRoomQuestion4,
    requestAudioReplay,
    requestHelp,
    confirmFinalGameAnswer,
    submitFinalGameAnswer,
    moveToNextFinalGameQuestion,
    setCurrentSession,
    setSelectedTeam,
    setFinalGameWinner
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
};

